import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Plane,
  ShoppingCart,
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../contexts/AuthContext';
import useRightSidebarData from '../../hooks/useRightSidebarData';
import LayoutComponent from './LayoutComponent';

const TABS = [
  { key: 'requests', label: 'Solicitudes', icon: ClipboardList },
  { key: 'notifications', label: 'Avisos', icon: Bell },
  { key: 'agenda', label: 'Agenda', icon: CalendarDays },
];

const greetingForHour = (hour) => {
  if (hour < 12) return 'Buenos días';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

const firstName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  return parts[0] || 'usuario';
};

const formatShortDate = (ymd) => {
  if (!ymd) return '';
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};

const SidebarRight = ({
  isDarkMode = false,
  collapsed = false,
  width = 320,
  onToggle,
  className = '',
  ...props
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentTheme = useTheme(isDarkMode);
  const [activeTab, setActiveTab] = useState('requests');

  const { isLoading, error, notifications, agenda } = useRightSidebarData({
    enabled: !collapsed,
    user,
  });

  const toggleHalf = 12;
  const border = currentTheme.colors.border.primary;
  const text = currentTheme.colors.text.primary;
  const muted = currentTheme.colors.text.secondary;
  const panel = currentTheme.colors.secondary;
  const surface = currentTheme.colors.primary;

  const sidebarCustomStyles = {
    right: 0,
    width: collapsed ? 0 : `${width}px`,
    height: 'calc(100vh - 64px)',
    padding: collapsed ? 0 : currentTheme.spacing.md,
    overflow: collapsed ? 'hidden' : 'auto',
    borderLeft: collapsed ? 'none' : `1px solid ${border}`,
    boxShadow: collapsed ? 'none' : currentTheme.colors.shadow.lg,
    pointerEvents: collapsed ? 'none' : 'auto',
    transition:
      'width 0.3s ease-in-out, padding 0.3s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
    ...props.style,
  };

  const toggleButtonStyles = {
    position: 'fixed',
    top: '50%',
    // Eje vertical central del botón sobre el borde (viewport o sidebar).
    right: collapsed ? -toggleHalf : width - toggleHalf,
    transform: 'translateY(-50%)',
    width: '24px',
    height: '80px',
    backgroundColor: surface,
    border: `2px solid ${border}`,
    borderRadius: currentTheme.borderRadius.md,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: text,
    transition: 'right 0.3s ease-in-out',
    zIndex: (currentTheme.zIndex?.fixed || 1030) + 15,
    boxShadow: currentTheme.colors.shadow.md,
    padding: 0,
    pointerEvents: 'auto',
  };

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    return `${greetingForHour(hour)}, ${firstName(user?.full_name)}`;
  }, [user?.full_name]);

  const go = (path) => {
    if (!path) return;
    navigate(path);
  };

  const requestShortcuts = [
    {
      key: 'rendicion',
      title: 'Rendición de gastos',
      subtitle: 'Enviar o revisar rendiciones',
      icon: FileText,
      onClick: () => go('/perfil?tab=solicitudes'),
      soon: false,
    },
    {
      key: 'viaje',
      title: 'Presupuesto de viaje',
      subtitle: 'Próximamente',
      icon: Plane,
      onClick: null,
      soon: true,
    },
    {
      key: 'compra',
      title: 'Solicitud de compra',
      subtitle: 'Ir a adquisiciones',
      icon: ShoppingCart,
      onClick: () => go('/backoffice/acquisitions/purchase-requests'),
      soon: false,
    },
    {
      key: 'agenda',
      title: 'Mi agenda',
      subtitle: 'Actividades, reuniones y to-do',
      icon: CalendarDays,
      onClick: () => go('/agenda'),
      soon: false,
    },
  ];

  const kindLabel = (kind) => {
    if (kind === 'crm') return 'CRM';
    if (kind === 'trip') return 'Viaje';
    if (kind === 'personal') return 'Agenda';
    return 'Despacho';
  };

  const severityStyles = (severity) => {
    if (severity === 'overdue') {
      return {
        bg: isDarkMode ? '#3f1d1d' : '#fef2f2',
        color: isDarkMode ? '#fca5a5' : '#b91c1c',
        border: isDarkMode ? '#7f1d1d' : '#fecaca',
      };
    }
    return {
      bg: isDarkMode ? '#1e3a5f' : '#eff6ff',
      color: isDarkMode ? '#93c5fd' : '#1d4ed8',
      border: isDarkMode ? '#1e40af' : '#bfdbfe',
    };
  };

  return (
    <>
      <LayoutComponent
        componentType="sidebar-right"
        isDarkMode={isDarkMode}
        className={className}
        customStyles={sidebarCustomStyles}
      >
        {!collapsed ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: currentTheme.spacing.md,
              minHeight: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: currentTheme.typography.fontSize.lg,
                  fontWeight: currentTheme.typography.fontWeight.semibold,
                  color: text,
                  lineHeight: 1.3,
                }}
              >
                {greeting}
              </div>
              <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                Accesos rápidos y tu agenda
              </div>
            </div>

            <div
              role="tablist"
              aria-label="Panel derecho"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 4,
                padding: 4,
                borderRadius: 10,
                background: panel,
                border: `1px solid ${border}`,
              }}
            >
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                const badge =
                  tab.key === 'notifications' && notifications.length
                    ? notifications.length
                    : null;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      padding: '8px 4px',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: active ? surface : 'transparent',
                      color: active ? text : muted,
                      boxShadow: active ? currentTheme.colors.shadow.sm : 'none',
                      fontSize: 10,
                      fontWeight: 600,
                      position: 'relative',
                    }}
                    title={tab.label}
                  >
                    <Icon size={16} />
                    <span>{tab.label}</span>
                    {badge ? (
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 6,
                          minWidth: 16,
                          height: 16,
                          borderRadius: 999,
                          background: '#dc2626',
                          color: '#fff',
                          fontSize: 9,
                          lineHeight: '16px',
                          textAlign: 'center',
                          padding: '0 4px',
                        }}
                      >
                        {badge > 9 ? '9+' : badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
              {activeTab === 'requests' ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {requestShortcuts.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        disabled={item.soon}
                        onClick={item.onClick || undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: `1px solid ${border}`,
                          background: surface,
                          color: text,
                          cursor: item.soon ? 'not-allowed' : 'pointer',
                          opacity: item.soon ? 0.65 : 1,
                        }}
                      >
                        <span
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: panel,
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={16} />
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 700 }}>
                            {item.title}
                          </span>
                          <span style={{ display: 'block', fontSize: 11, color: muted, marginTop: 2 }}>
                            {item.subtitle}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {activeTab === 'notifications' ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {isLoading ? (
                    <div style={{ fontSize: 12, color: muted, padding: 8 }}>Cargando avisos…</div>
                  ) : null}
                  {error ? (
                    <div style={{ fontSize: 12, color: '#dc2626', padding: 8 }}>{error}</div>
                  ) : null}
                  {!isLoading && !error && notifications.length === 0 ? (
                    <div style={{ fontSize: 12, color: muted, padding: 8 }}>
                      No tienes notificaciones.
                    </div>
                  ) : null}
                  {notifications.map((item) => {
                    const tone = severityStyles(item.severity);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => go(item.href)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: `1px solid ${tone.border}`,
                          background: tone.bg,
                          color: tone.color,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{item.title}</div>
                        <div style={{ fontSize: 11, marginTop: 2, opacity: 0.9 }}>{item.subtitle}</div>
                        <div style={{ fontSize: 10, marginTop: 6, opacity: 0.8 }}>
                          {item.severity === 'overdue' ? 'Vencida' : 'Próxima'} · {formatShortDate(item.date)} ·{' '}
                          {kindLabel(item.kind)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {activeTab === 'agenda' ? (
                <div style={{ display: 'grid', gap: 14 }}>
                  {isLoading ? (
                    <div style={{ fontSize: 12, color: muted, padding: 8 }}>Cargando agenda…</div>
                  ) : null}
                  {[
                    { key: 'today', label: 'Hoy', items: agenda.today, date: agenda.todayLabel },
                    {
                      key: 'tomorrow',
                      label: 'Mañana',
                      items: agenda.tomorrow,
                      date: agenda.tomorrowLabel,
                    },
                  ].map((section) => (
                    <div key={section.key}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          marginBottom: 8,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: text }}>
                          {section.label}
                        </span>
                        <span style={{ fontSize: 10, color: muted }}>
                          {formatShortDate(section.date)}
                        </span>
                      </div>
                      {section.items.length === 0 ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: muted,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: `1px dashed ${border}`,
                          }}
                        >
                          Sin actividades
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 6 }}>
                          {section.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => go(item.href)}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${border}`,
                                background: surface,
                                color: text,
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{item.title}</div>
                              {item.subtitle ? (
                                <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                                  {item.subtitle}
                                </div>
                              ) : null}
                              <div style={{ fontSize: 10, color: muted, marginTop: 4 }}>
                                {kindLabel(item.kind)}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => go('/agenda')}
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: `1px solid ${border}`,
                      background: 'transparent',
                      color: text,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Abrir mi agenda
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </LayoutComponent>

      {onToggle ? (
        <button
          type="button"
          style={toggleButtonStyles}
          onClick={onToggle}
          title={collapsed ? 'Mostrar panel' : 'Ocultar panel'}
          aria-label={collapsed ? 'Mostrar panel derecho' : 'Ocultar panel derecho'}
        >
          {collapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      ) : null}
    </>
  );
};

export default SidebarRight;
